/**
 * cart-tag-limit.js
 *
 * Enforces "at most one item tagged 'Pack' per cart" on the storefront
 * (quantity counts). Liquid knows product tags (/cart.js does not), so the
 * total tagged quantity is computed server-side in cart-line-items.liquid and
 * rendered as a hidden [data-cart-limit] marker. This script reads that marker
 * and enforces three things:
 *
 *   1. Disables the add-to-cart button on ANY tagged product once the cart
 *      already holds a tagged item (the main behavior).
 *   2. Click guard — also blocks an add that would push the tagged total over
 *      the cap (e.g. adding qty 2 of the first tagged item in one click).
 *   3. Checkout guard — disables the cart "Checkout" button and shows a
 *      message if the cart's tagged total ever exceeds the cap.
 *
 * The tag string lives only in the Liquid. Inspect live state in the console
 * via `window.cartTagLimit`.
 *
 * NOTE: This is the friendly storefront layer and is bypassable. The
 * unbypassable backstop is the Cart Validation Function in the spend-cap app.
 */
(function () {
  'use strict';

  var ADD_MESSAGE =
    'You already have one of these in your cart. Only one can be purchased per order.';
  var CHECKOUT_MESSAGE =
    'Only one of these items can be purchased per order. Please reduce the quantity to one to continue.';
  // English fallback only. The real, translated string is rendered onto each
  // button as data-cart-limit-label ('products.product.pack_added_to_cart').
  var AT_CAP_LABEL_FALLBACK = 'Pack Added to Cart';
  var MSG_STYLE =
    'margin:10px 0;padding:10px 12px;border-radius:6px;background:#fdecea;color:#611a15;font-size:14px;line-height:1.4;';

  var currentLimitedQty = 0;
  var limitMax = 1;

  // Expose state for debugging in the browser console.
  window.cartTagLimit = {
    get currentLimitedQty() { return currentLimitedQty; },
    get limitMax() { return limitMax; },
    refresh: function () { return refreshAndApply(); },
  };

  // Fetch the cart-items section fresh and read the marker. Authoritative and
  // independent of the theme's own re-render timing.
  function refreshFromSection() {
    var root =
      (window.theme && window.theme.routes && window.theme.routes.root_url) || '';
    return fetch(root + '?section_id=api-cart-items')
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var div = document.createElement('div');
        div.innerHTML = html;
        readMarkerFrom(div);
      })
      .catch(function () {});
  }

  function readMarkerFrom(scope) {
    var marker = scope.querySelector('[data-cart-limit]');
    if (!marker) return false;
    currentLimitedQty = parseInt(marker.getAttribute('data-limited-qty'), 10) || 0;
    limitMax = parseInt(marker.getAttribute('data-limit-max'), 10) || 1;
    return true;
  }

  function buildMessage(text) {
    var el = document.createElement('div');
    el.setAttribute('data-cart-limit-message', '');
    el.setAttribute('role', 'alert');
    el.textContent = text;
    el.style.cssText = MSG_STYLE;
    return el;
  }

  // --- Apply state to the DOM -------------------------------------------------

  function applyState() {
    // At/over the cap -> no more tagged items may be added.
    var atCap = currentLimitedQty >= limitMax;
    // Strictly over the cap -> the cart itself is invalid, block checkout.
    var overCap = currentLimitedQty > limitMax;

    // 1. Mark add-to-cart buttons on tagged products as unavailable.
    //    We deliberately do NOT touch the `disabled` property here: the theme's
    //    Alpine binding rewrites it from variant availability (it would both
    //    undo our disable and un-disable a sold-out button). The class is the
    //    visual state; onAddClick is what actually blocks the add.
    //    No message is rendered here either — the cart hits the cap on the
    //    customer's own first, legitimate add, and a red note at that moment
    //    reads as "your add failed" when it in fact succeeded. The message is
    //    shown only when a click is really blocked.
    var addButtons = document.querySelectorAll(
      '[data-add-to-cart][data-cart-limited="true"]'
    );
    for (var i = 0; i < addButtons.length; i++) {
      var btn = addButtons[i];
      btn.classList.toggle('cart-limit-disabled', atCap);
      btn.setAttribute('aria-disabled', atCap ? 'true' : 'false');
      setAtCapLabel(btn, atCap);
      if (!atCap) toggleNoteNear(btn, false, ADD_MESSAGE);
    }

    // 3. Block checkout if the cart is already over the cap. Only ever set
    //    `disabled` ourselves when blocking, and only clear the flag we set,
    //    so we never re-enable a checkout button something else disabled.
    var checkoutButtons = document.querySelectorAll('[name="checkout"]');
    for (var j = 0; j < checkoutButtons.length; j++) {
      var cbtn = checkoutButtons[j];
      if (overCap) {
        cbtn.disabled = true;
        cbtn.setAttribute('data-cart-limit-blocked', '');
      } else if (cbtn.hasAttribute('data-cart-limit-blocked')) {
        cbtn.disabled = false;
        cbtn.removeAttribute('data-cart-limit-blocked');
      }
      cbtn.classList.toggle('cart-limit-blocked', overCap);
      toggleNoteNear(cbtn, overCap, CHECKOUT_MESSAGE);
    }
  }

  // Swap the button label to the at-cap state and back. The original label is
  // stashed on the element the first time we touch it so a later variant
  // re-render can't strand us with the wrong text.
  function setAtCapLabel(btn, atCap) {
    var textEl = btn.querySelector('[data-add-to-cart-text]');
    if (!textEl) return;

    var label =
      (btn.getAttribute('data-cart-limit-label') || '').trim() ||
      AT_CAP_LABEL_FALLBACK;

    if (atCap) {
      if (!textEl.hasAttribute('data-cart-limit-orig')) {
        textEl.setAttribute('data-cart-limit-orig', textEl.textContent);
      }
      if (textEl.textContent.trim() !== label) {
        textEl.textContent = label;
      }
      btn.setAttribute('aria-label', label);
    } else if (textEl.hasAttribute('data-cart-limit-orig')) {
      var orig = textEl.getAttribute('data-cart-limit-orig');
      textEl.textContent = orig;
      textEl.removeAttribute('data-cart-limit-orig');
      btn.setAttribute('aria-label', orig.trim());
    }

    // The price readout next to the label makes no sense on "Pack Added to
    // Cart" — hide it while we own the label.
    var extras = btn.querySelectorAll('.cta__dot, [data-button-price]');
    for (var k = 0; k < extras.length; k++) {
      extras[k].style.display = atCap ? 'none' : '';
    }
  }

  // Insert/remove a message immediately before the given button.
  function toggleNoteNear(btn, show, text) {
    var holder = btn.parentNode;
    if (!holder) return;
    var existing = holder.querySelector(':scope > [data-cart-limit-message]');
    if (show && !existing) {
      holder.insertBefore(buildMessage(text), btn);
    } else if (!show && existing) {
      existing.remove();
    }
  }

  // --- Click guard (catches qty>1 on the first tagged add) --------------------

  function onAddClick(e) {
    var target = e.target;
    var btn = target.closest && target.closest('[data-add-to-cart]');
    if (!btn || btn.getAttribute('data-cart-limited') !== 'true') return;

    var form = btn.closest('[data-product-form]');
    var qtyInput = form && form.querySelector('[name="quantity"]');
    var adding = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

    if (currentLimitedQty + adding > limitMax) {
      // Capture phase: stop the theme's own (bubbling) add-to-cart handler.
      e.preventDefault();
      e.stopImmediatePropagation();
      var holder = (form && form.querySelector('[data-error-display]')) || btn.parentNode;
      if (holder && !holder.querySelector('[data-cart-limit-message]')) {
        var msg = buildMessage(ADD_MESSAGE);
        holder.appendChild(msg);
        setTimeout(function () { if (msg.parentNode) msg.parentNode.removeChild(msg); }, 6000);
      }
      return;
    }

    // Allowed add: the theme's own theme:cart:change does not reliably reach
    // this listener from the product form, so re-read the cart ourselves once
    // the add has had time to land.
    setTimeout(refreshAndApply, 900);
    setTimeout(refreshAndApply, 2500);
  }

  // --- Wiring -----------------------------------------------------------------

  // The theme owns the button's `disabled` property, so the at-cap look is a
  // class of our own.
  (function injectStyle() {
    var css =
      '[data-add-to-cart].cart-limit-disabled{opacity:.5;cursor:not-allowed;}';
    var style = document.createElement('style');
    style.setAttribute('data-cart-limit-style', '');
    style.appendChild(document.createTextNode(css));
    (document.head || document.documentElement).appendChild(style);
  })();

  // Capture phase so we run before the theme's add-to-cart handler.
  document.addEventListener('click', onAddClick, true);

  function refreshAndApply() {
    return refreshFromSection().then(applyState);
  }

  var CART_EVENTS = [
    'theme:cart:change',
    'theme:cart:reload',
    'theme:cart:add',
    'theme:cart:update',
    'theme:cart:popdown',
    'cartAdded',
  ];
  for (var e = 0; e < CART_EVENTS.length; e++) {
    document.addEventListener(CART_EVENTS[e], refreshAndApply);
  }

  if (document.readyState !== 'loading') {
    refreshAndApply();
  } else {
    document.addEventListener('DOMContentLoaded', refreshAndApply);
  }
})();
